import { forwardRef, useEffect, useMemo, useState } from "react";
import { Source, Layer } from "react-map-gl";
import BaseMap from "./BaseMap";

const SPAN_OFFSET_PX = 6;

function generateSpanFeature(span, structures, offset) {
    const startStructure = structures.find((s) => s.structure_name === span.start_structure);
    const endStructure = structures.find((s) => s.structure_name === span.end_structure);

    if (!startStructure || !endStructure) return null;

    return {
        type: "Feature",
        geometry: {
            type: "LineString",
            coordinates: [
                [startStructure.longitude, startStructure.latitude],
                [endStructure.longitude, endStructure.latitude],
            ],
        },
        properties: {
            description: `Span: ${span.start_structure}-${span.end_structure} (${span.section_name})`,
            latitude: span.latitude,
            longitude: span.longitude,
            offset,
        },
    };
}

// clientAssetData (legacy) already stores spans/structures/sections as flat records
// with their own lat/lon fields. rootGeoJSON (newer) stores the same information as
// GeoJSON features keyed by properties.feature_type. Normalize both shapes down to
// the { spans, structures, sections } record arrays consumers expect
export function normalizeClientAssetData(clientAssetData) {
    if (!clientAssetData) return null;
    const { spans, structures, sections = [] } = clientAssetData.data;
    return { spans, structures, sections };
}

export function normalizeRootGeoJSON(rootGeoJSON) {
    if (!rootGeoJSON) return null;

    const structures = rootGeoJSON.features
        .filter((feature) => feature.properties?.feature_type === "structure")
        .map((feature) => ({
            structure_name: feature.properties.structure_name,
            longitude: feature.geometry.coordinates[0],
            latitude: feature.geometry.coordinates[1],
            dead_end: feature.properties.dead_end,
        }));

    const spans = rootGeoJSON.features
        .filter((feature) => feature.properties?.feature_type === "span")
        .map((feature) => ({
            start_structure: feature.properties.start_structure,
            end_structure: feature.properties.end_structure,
            section_name: feature.properties.section_name,
        }));

    const sections = rootGeoJSON.features
        .filter((feature) => feature.properties?.feature_type === "section")
        .map((feature) => ({
            section_name: feature.properties.section_name,
            line_name: feature.properties.line_name,
            start_structure: feature.properties.start_structure,
            end_structure: feature.properties.end_structure,
        }));

    return { spans, structures, sections };
}

function structurePairKey(span) {
    // Normalize direction so A-B and B-A are the same key
    const [a, b] = [span.start_structure, span.end_structure].sort();
    return `${a}__${b}`;
}

function assignSpanOffsets(spans) {
    // Group spans by their structure pair
    const groups = {};
    for (const span of spans) {
        const key = structurePairKey(span);
        if (!groups[key]) groups[key] = [];
        groups[key].push(span);
    }

    // For each group, spread offsets symmetrically around 0:
    // 1 span  → [0]
    // 2 spans → [-OFFSET, +OFFSET]
    // 3 spans → [-OFFSET, 0, +OFFSET]
    return spans.map((span) => {
        const group = groups[structurePairKey(span)];
        const count = group.length;
        const index = group.indexOf(span);
        const offset = (index - (count - 1) / 2) * SPAN_OFFSET_PX;
        return { span, offset };
    });
}

const ClientAssetDataMap = forwardRef(function ClientAssetDataMap(
    { rootGeoJSON = null, clientAssetData = null, hideSpans = false, hideStructures = false, children, interactiveLayerIds = [], ...baseMapProps },
    ref
) {
    const [spansGeoJSON, setSpansGeoJSON] = useState(null);
    const [structuresGeoJSON, setStructuresGeoJSON] = useState(null);

    const assetData = useMemo(
        () => normalizeRootGeoJSON(rootGeoJSON) ?? normalizeClientAssetData(clientAssetData),
        [rootGeoJSON, clientAssetData]
    );

    useEffect(() => {
        if (!assetData) {
            setSpansGeoJSON(null);
            setStructuresGeoJSON(null);
            return;
        }

        const { spans, structures } = assetData;

        setSpansGeoJSON({
            type: "FeatureCollection",
            features: assignSpanOffsets(spans)
                .map(({ span, offset }) => generateSpanFeature(span, structures, offset))
                .filter(Boolean),
        });

        setStructuresGeoJSON({
            type: "FeatureCollection",
            features: structures.map((structure) => ({
                type: "Feature",
                geometry: {
                    type: "Point",
                    coordinates: [structure.longitude, structure.latitude],
                },
                properties: {
                    description: `Structure: ${structure.structure_name}`,
                    latitude: structure.latitude,
                    longitude: structure.longitude,
                    dead_end: structure.dead_end ?? false,
                },
            })),
        });
    }, [assetData]);

    const cadInteractiveLayerIds = assetData
        ? [...(!hideSpans ? ["cad-span-layer"] : []), ...(!hideStructures ? ["cad-structure-hit-layer"] : [])]
        : [];

    return (
        <BaseMap ref={ref} interactiveLayerIds={[...cadInteractiveLayerIds, ...interactiveLayerIds]} {...baseMapProps}>
            {assetData && (
                <>
                    <Source
                        id="cad-spans"
                        type="geojson"
                        data={spansGeoJSON ?? { type: "FeatureCollection", features: [] }}
                    >
                        {/* Yellow lines connecting structure pairs; offset so parallel spans don't overlap */}
                        <Layer
                            id="cad-span-layer"
                            type="line"
                            paint={{
                                "line-color": "#FFFF00",
                                "line-width": 2.5,
                                "line-offset": ["get", "offset"],
                                "line-opacity": hideSpans ? 0 : 1,
                            }}
                        />
                    </Source>
                    <Source
                        id="cad-structures"
                        type="geojson"
                        data={structuresGeoJSON ?? { type: "FeatureCollection", features: [] }}
                    >
                        {/* Dark blue circle outlines marking each structure's position */}
                        <Layer
                            id="cad-structure-layer"
                            type="circle"
                            paint={{
                                "circle-radius": 6,
                                "circle-color": "rgba(0, 0, 0, 0)",
                                "circle-stroke-color": "#003087",
                                "circle-stroke-width": 2,
                                "circle-stroke-opacity": hideStructures ? 0 : 1,
                            }}
                        />
                        {/* Invisible larger circle used as a click/hover hit target for structures */}
                        <Layer
                            id="cad-structure-hit-layer"
                            type="circle"
                            paint={{
                                "circle-radius": 9,
                                "circle-opacity": 0,
                                "circle-stroke-width": 0,
                            }}
                        />
                    </Source>
                </>
            )}
            {children}
        </BaseMap>
    );
});

export default ClientAssetDataMap;
